import { cn } from "@/lib/utils";

interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  lede?: string;
  align?: "left" | "center";
  as?: "h1" | "h2" | "h3";
  className?: string;
  id?: string;
}

/**
 * Brand section grammar: driftwood eyebrow → Bodoni headline → short lede.
 * Use on every guest-facing section for consistent editorial rhythm.
 */
export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = "center",
  as: Tag = "h2",
  className,
  id,
}: SectionHeadingProps) {
  return (
    <div
      className={cn(
        "max-w-2xl mb-10 md:mb-14",
        align === "center" ? "mx-auto text-center" : "text-left",
        className,
      )}
    >
      {eyebrow && (
        <p className="text-[11px] md:text-xs font-sans font-semibold uppercase tracking-[0.3em] text-driftwood mb-3">
          {eyebrow}
        </p>
      )}
      <Tag
        id={id}
        className="font-serif font-bold text-forest tracking-luxury-tight leading-[1.05] text-luxury-3xl md:text-luxury-4xl"
      >
        {title}
      </Tag>
      {/* Brand diamond motif — a quiet, recurring signature under the title */}
      <div className={cn("flex items-center gap-2.5 mt-5", align === "center" ? "justify-center" : "justify-start")}>
        <span className="h-px w-9 bg-sand-deep" />
        <span className="w-[6px] h-[6px] rotate-45 bg-driftwood/50" />
        <span className="h-px w-9 bg-sand-deep" />
      </div>
      {lede && (
        <p className="mt-4 text-luxury-base md:text-luxury-lg text-ink-soft leading-relaxed">
          {lede}
        </p>
      )}
    </div>
  );
}

export default SectionHeading;
